/**
 * "Definir a empresa do negócio" (13.20b2) — a primeira Empresa do Negócio sem Empresa.
 *
 * No molde de `TrocaDaEmpresaDoNegocio` (13.5): o `CampoDeEmpresa` da 13.1 com sugestão (quem
 * recorta é a RLS do `cadastro`) e "Criar nova empresa", que abre a ficha do "+ Empresa"
 * (`ModalDaNovaEmpresa`) com o Responsável do Negócio como Responsável inicial. Quem não é gestão
 * não escolhe o Responsável da ficha nova (ela nasce com quem a cadastra, FR-33) — o "fixo para quem
 * não é gestão" da spec, que a ficha já faz.
 *
 * - **Só define** (`PUT /negocios/{id}/definicao-da-empresa`): o contato do Negócio é vinculado à
 *   Empresa pelo `cadastro`, ao consumir o evento — quem abre este diálogo espera o vínculo
 *   (`useEsperaDoVinculo`) e mostra "vinculando…".
 * - **A recusa vai verbatim e o diálogo fica aberto**: o `409` da Empresa fora do recorte de quem
 *   pede ("as suas e as do Grupo"), o `409` do Negócio que já tem Empresa, o `404` da Empresa que não
 *   chegou a tempo. Quem age escolhe outra.
 * - **Esconder o botão é conveniência**: quem recusa é o serviço (`403`/`409`).
 *
 * A ficha empilhada é **irmã** do diálogo, e não filha: o `Esc` e o clique no fundo dela não fecham
 * a definição de baixo (o `Dialogo` cede o `Esc` a quem está por cima).
 */

import { useEffect, useId, useState } from 'react'

import { ErroDaApi } from '@/api/erros'

import { useEmpresa } from '@/modulos/cadastro/api'
import { CampoDeEmpresa, type EmpresaEscolhida } from '@/modulos/cadastro/CampoDeEmpresa'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { ModalDaNovaEmpresa } from '@/modulos/cadastro/ModalDaNovaEmpresa'
import { useDefinirAEmpresa } from './api'
import { Dialogo } from './Dialogo'

export const TITULO_DA_DEFINICAO = 'Definir a empresa do negócio'
export const ROTULO_DO_DEFINIR = 'Definir'
/** O que acontece depois de definir: o contato vai para a Empresa, sozinho, em alguns segundos. */
export const NOTA_DA_DEFINICAO =
  'O contato do negócio passa a ser desta empresa em alguns segundos. Depois de definida, só a ' +
  'gestão troca a empresa.'
/**
 * Code review (B5): a nota do SPA ao lado do `404` (que continua verbatim) quando a Empresa acabou
 * de nascer na ficha empilhada — a réplica do `negocios` ainda não a conhece.
 */
export const NOTA_DA_EMPRESA_RECEM_CADASTRADA =
  'A empresa acabou de ser cadastrada; tente Definir de novo em instantes.'
/** Code review (B5): o ERP achou uma ficha que quem age não vê — a escolha volta a vazio. */
export const EMPRESA_ENCONTRADA_INVISIVEL =
  'A empresa deste CNPJ já está cadastrada, mas não está visível para você. Escolha outra ' +
  'empresa ou peça à gestão.'
/** O nome enquanto a ficha achada pelo ERP ainda está sendo lida. */
const EMPRESA_JA_CADASTRADA = 'a empresa já cadastrada'

/** O anúncio do sucesso — com o nome da Empresa definida (13.7: o nome exibido). */
export function textoDaDefinicao(nomeDaEmpresa: string): string {
  return `Empresa do negócio definida: ${nomeDaEmpresa}.`
}

export interface PropriedadesDaDefinicao {
  negocioId: string
  /** O subtítulo do diálogo: o nome do contato, ou "Negócio <código>". */
  subtitulo: string
  /** O Responsável do Negócio — o inicial da Empresa criada aqui (para a gestão). */
  responsavelDoNegocio: string
  /**
   * Se a ficha (relida) ainda está sem Empresa. O `409` "já tem Empresa" relê a ficha; quando ela
   * chega com a Empresa, o diálogo fecha (code review, B5).
   */
  semEmpresa: boolean
  aoFechar: () => void
  /** A definição deu certo: a Empresa escolhida, para o anúncio e a espera do vínculo. */
  aoDefinida: (empresa: EmpresaEscolhida) => void
}

export function DefinicaoDaEmpresaDoNegocio({
  negocioId,
  subtitulo,
  responsavelDoNegocio,
  semEmpresa,
  aoFechar,
  aoDefinida,
}: PropriedadesDaDefinicao) {
  const idDoCampo = useId()
  const definir = useDefinirAEmpresa(negocioId)
  const [escolhida, setEscolhida] = useState<EmpresaEscolhida | null>(null)
  const [fichaAberta, setFichaAberta] = useState<string | null>(null)
  const [chaveDoCampo, setChaveDoCampo] = useState(0)
  /* A escolhida nasceu agora na ficha empilhada: o `404` dela ganha a nota do SPA. */
  const [recemCriada, setRecemCriada] = useState(false)
  const [encontradaInvisivel, setEncontradaInvisivel] = useState(false)

  /* A ficha que o ERP achou (`aoEncontrar`) chega só com o `id`: o nome vem da leitura dela. */
  const semNome = escolhida !== null && escolhida.nome === ''
  const lida = useEmpresa(semNome ? escolhida.id : undefined)
  /* A ficha que o ERP achou e que quem age não vê (`403`/`404` na leitura): a escolha volta a vazio
     e a tela explica — senão o "Definir" ficaria preso esperando um nome que não vem. */
  if (
    semNome &&
    lida.error instanceof ErroDaApi &&
    [403, 404].includes(lida.error.status)
  ) {
    setEscolhida(null)
    setEncontradaInvisivel(true)
  }
  const recusaDaDefinicao = definir.error instanceof ErroDaApi ? definir.error : null
  /* O `409` "já tem Empresa": a ficha relida chega com a Empresa, e o diálogo fecha. */
  const jaTemEmpresa = recusaDaDefinicao?.status === 409 && !semEmpresa
  useEffect(() => {
    if (jaTemEmpresa) {
      aoFechar()
    }
  }, [jaTemEmpresa, aoFechar])
  const nomeEscolhido =
    escolhida === null
      ? ''
      : escolhida.nome ||
        (lida.data === undefined ? EMPRESA_JA_CADASTRADA : nomeExibidoDaEmpresa(lida.data))
  const lendoONome = semNome && lida.data === undefined
  const confirmar = () => {
    if (escolhida === null || lendoONome) {
      return
    }
    definir.mutate(escolhida.id, {
      onSuccess: () => {
        aoDefinida({ id: escolhida.id, nome: nomeEscolhido })
      },
    })
  }

  return (
    <>
      <Dialogo
        titulo={TITULO_DA_DEFINICAO}
        subtitulo={subtitulo}
        rotuloDoConfirmar={ROTULO_DO_DEFINIR}
        confirmarDesabilitado={escolhida === null || lendoONome}
        ocupado={definir.isPending}
        erro={definir.isError ? definir.error : undefined}
        aoFechar={aoFechar}
        aoConfirmar={confirmar}
      >
        <CampoDeEmpresa
          key={chaveDoCampo}
          id={idDoCampo}
          escolhida={escolhida === null ? null : { id: escolhida.id, nome: nomeEscolhido }}
          aoEscolher={(empresa) => {
            definir.reset()
            setRecemCriada(false)
            setEncontradaInvisivel(false)
            setEscolhida(empresa)
          }}
          aoCriarNova={(texto) => {
            setFichaAberta(texto)
          }}
          //: A lista por cima do corpo curto do diálogo, nunca cortada (o portal da 13.18).
          flutuante
        />
        {recemCriada && recusaDaDefinicao?.status === 404 ? (
          <p className="ficha__dialogo-aviso">{NOTA_DA_EMPRESA_RECEM_CADASTRADA}</p>
        ) : null}
        {encontradaInvisivel ? (
          <p className="ficha__dialogo-aviso" role="alert">
            {EMPRESA_ENCONTRADA_INVISIVEL}
          </p>
        ) : null}
        <p className="ficha__dialogo-aviso">{NOTA_DA_DEFINICAO}</p>
      </Dialogo>
      {fichaAberta === null ? null : (
        <ModalDaNovaEmpresa
          textoInicial={fichaAberta}
          telefone={undefined}
          responsavelInicial={responsavelDoNegocio}
          aoCancelar={() => {
            setFichaAberta(null)
          }}
          aoEscolher={(empresa) => {
            definir.reset()
            setRecemCriada(true)
            setEncontradaInvisivel(false)
            setEscolhida(empresa)
            setChaveDoCampo((chave) => chave + 1)
            setFichaAberta(null)
          }}
          aoEncontrar={(empresaId) => {
            //: O CNPJ que o ERP já conhece: a ficha existente fica escolhida, e o nome vem da
            //: leitura dela (`useEmpresa`).
            definir.reset()
            setRecemCriada(false)
            setEncontradaInvisivel(false)
            setEscolhida({ id: empresaId, nome: '' })
            setChaveDoCampo((chave) => chave + 1)
            setFichaAberta(null)
          }}
        />
      )}
    </>
  )
}
